import { MapBounds } from '../types';

/**
 * Calculates how much of `next` viewport is covered by `prev` viewport.
 * Returns 0..1 where 1 means next is fully contained within prev.
 */
export const boundsOverlapRatio = (prev: MapBounds, next: MapBounds): number => {
  const overlapLat = Math.max(0, Math.min(prev.latMax, next.latMax) - Math.max(prev.latMin, next.latMin));
  const overlapLon = Math.max(0, Math.min(prev.lonMax, next.lonMax) - Math.max(prev.lonMin, next.lonMin));
  const overlapArea = overlapLat * overlapLon;
  const nextArea = (next.latMax - next.latMin) * (next.lonMax - next.lonMin);
  return nextArea > 0 ? overlapArea / nextArea : 0;
};

/**
 * Rutenett-størrelse (grader) for CDN-kvantisering av markør-henting, ut fra zoom.
 *
 * Grovere rutenett = flere brukere deler samme URL = høyere CDN-treffrate. Men
 * kvantiseringen UTVIDER utsnittet, og siden markørspørringen har et fast
 * dokumenttak (1500/3000) blir treffene tynnere per grad når området vokser.
 * Rutenettet følger derfor zoom, slik at utvidelsen holder seg liten i forhold
 * til utsnittet og dekningen ikke blir dårligere enn før.
 */
export const cacheGridForZoom = (zoom: number): number => {
  if (zoom >= 13) return 0.05;
  if (zoom >= 11) return 0.1;
  if (zoom >= 9) return 0.25;
  return 0.5;
};

/**
 * Utvider et utsnitt utover til nærmeste rutenettlinjer, slik at nærliggende
 * kartvisninger får identisk URL og kan treffe CDN-cachen.
 *
 * Resultatet er ALLTID et supersett av inn-utsnittet — aldri mindre — så ingen
 * plasser kan falle ut av visningen som følge av kvantiseringen.
 *
 * Verdiene rundes til 6 desimaler fordi `Math.floor(59.15 / 0.05) * 0.05` gir
 * 59.150000000000006 i flyttall. Uten avrundingen ville URL-en variert mellom
 * klienter og cache-nøkkelen aldri kollidert — altså null CDN-effekt.
 */
export const snapBoundsOutward = (bounds: MapBounds, gridSize: number): MapBounds => {
  const round6 = (v: number) => Math.round(v * 1e6) / 1e6;
  const down = (v: number) => round6(Math.floor(v / gridSize) * gridSize);
  const up = (v: number) => round6(Math.ceil(v / gridSize) * gridSize);
  return {
    latMin: Math.max(-90, down(bounds.latMin)),
    latMax: Math.min(90, up(bounds.latMax)),
    lonMin: Math.max(-180, down(bounds.lonMin)),
    lonMax: Math.min(180, up(bounds.lonMax)),
  };
};

/**
 * Snaps a bounding box to a grid for stable cache keys.
 * @param gridSize Grid cell size in degrees (0.05 ≈ 5km)
 */
export const snapBboxToGrid = (bounds: MapBounds, gridSize: number): string => {
  const snap = (v: number, roundDown: boolean) =>
    roundDown ? Math.floor(v / gridSize) * gridSize : Math.ceil(v / gridSize) * gridSize;
  return `${snap(bounds.latMin, true).toFixed(3)}_${snap(bounds.lonMin, true).toFixed(3)}_${snap(bounds.latMax, false).toFixed(3)}_${snap(bounds.lonMax, false).toFixed(3)}`;
};
